// dataGrid.jsx — cleaned & minimal-styled

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css';
import { renderDiscoveryRuleDashboardFromJSON } from '@process-advisor/process-miner/dashboard/discoveryRules/discoveryRule_renderer';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getAppVersion, getIssueTypes, taskJSON, getSNOWApi } from '@process-advisor/process-miner/processMiner/processMiner';

import DecomposeIcon from '@process-advisor/assets/decomposeIcon.png';
import ClusterIcon from '@process-advisor/assets/clusterIcon.png';
import InfluencerIcon from '@process-advisor/assets/influencerIcon.png';
import AnalyticsIcon from '@process-advisor/assets/analyticsIcon.png';
import HistogramIcon from '@process-advisor/assets/histogramIcon.png';
import TaskIcon from '@process-advisor/assets/taskIcon.png';
import DetailIcon from '@process-advisor/assets/detailIcon.png';
import TaskLifeCycleIcon from '@process-advisor/assets/taskLifecycleIcon.png';
import RefreshIcon from '@process-advisor/assets/refreshIcon.png';
import ExportIcon from '@process-advisor/assets/export.png';
import CSVIcon from '@process-advisor/assets/CSVIcon.png';
import ExcelIcon from '@process-advisor/assets/XLSIcon.png';
import SaveIcon from '@process-advisor/assets/save.png';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// -------- minimal styled-components --------
const Wrapper = styled.div`
  height: 550px;
  width: 100%;
`;

const GridWrapper = styled.div`
  height: 100%;
  width: 100%;

  .ag-theme-alpine {
    --ag-header-background-color: #f4f5f7;
    --ag-header-foreground-color: #172b4d;
    --ag-border-color: #dfe1e6;
    --ag-row-hover-color: #f4f5f7;
    --ag-selected-row-background-color: #e9f2ff;
    --ag-font-size: 14px;
    --ag-header-font-weight: 600;
    --ag-cell-horizontal-padding: 12px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  background: #0052cc;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
  &:hover { background: #0747a6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const CloseButton = styled.button`
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const Content = styled.div`
  padding: 20px;
  background: #f4f5f7;
  flex: 1;
  overflow: auto;
`;

const ExportSheet = styled.div`
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(9,30,66,0.25);
  width: 440px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ExportHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #dfe1e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ExportTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #172b4d;
  margin: 0;
`;

const ExportBody = styled.div`
  padding: 16px 20px;
`;

const ExportLabel = styled.label`
  font-size: 14px;
  color: #172b4d;
  margin-bottom: 10px;
  display: block;
`;

const ExportOption = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 2px solid #dfe1e6;
  border-radius: 6px;
  margin-bottom: 10px;
  background: #fff;
  cursor: pointer;
  transition: all .2s;
  &:hover { border-color: #1868db; background: #f4f5f7; }
`;

const ExportIconSvg = styled.svg`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const ExportText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExportName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #172b4d;
`;

const ExportDesc = styled.span`
  font-size: 12px;
  color: #6b778c;
`;

const ExportFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #dfe1e6;
`;

const CancelButton = styled.button`
  padding: 8px 12px;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

// -------- utils --------
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const traceSaveDiscoveryRule = (event, data = {}) => {
  const entry = {
    event,
    at: new Date().toISOString(),
    ...data,
  };
  try {
    console.log('[PA Save Discovery Rule]', entry);
  } catch (_) {}
  try {
    window.__PA_SAVE_DISCOVERY_RULE_TRACE__ = Array.isArray(window.__PA_SAVE_DISCOVERY_RULE_TRACE__)
      ? window.__PA_SAVE_DISCOVERY_RULE_TRACE__.slice(-99).concat(entry)
      : [entry];
    window.__PA_LAST_SAVE_DISCOVERY_RULE_TRACE__ = entry;
  } catch (_) {}
  return entry;
};

const safeDebugPreview = (value, max = 1200) => {
  try {
    const text = JSON.stringify(value, null, 2);
    return text.length > max ? `${text.slice(0, max)}... [truncated]` : text;
  } catch (err) {
    return String(value);
  }
};

const asBoolInt = (value) => value === true || value === 1 || String(value || '').toLowerCase() === 'true' ? 1 : 0;

const cloneDiscoveryRuleForGlobalSave = (discoveryRule = {}, type = 'generic') => {
  const id = uuidv4();
  const filters = Array.isArray(discoveryRule.filters)
    ? JSON.parse(JSON.stringify(discoveryRule.filters))
    : [];

  if (String(discoveryRule.type || '').toLowerCase() === 'discovery') {
    filters.forEach((filter) => {
      filter.steps = [];
      filter.activities = [];
      filter.variants = [];
    });
  }

  const getCurrentRecordType = () => {
    if (type !== 'issueType') return 'Generic';
    const issueTypes = typeof apiProvider.getIssueTypes === 'function' ? apiProvider.getIssueTypes() : null;
    if (Array.isArray(issueTypes) && issueTypes.length) return issueTypes.join(' | ');
    if (typeof issueTypes === 'string' && issueTypes.trim()) return issueTypes.trim();
    const taskTable = typeof apiProvider.getTaskTable === 'function' ? apiProvider.getTaskTable() : null;
    return taskTable?.taskTable || taskTable?.table || 'Generic';
  };
  const issueType = getCurrentRecordType();
  return {
    id,
    discovery_rule: discoveryRule.discovery_rule,
    category: discoveryRule.category || 'Performance',
    type: discoveryRule.type || discoveryRule.discovery_rule_type || 'Discovery',
    discovery_rule_type: discoveryRule.discovery_rule_type || discoveryRule.type || 'Discovery',
    automation_opportunity: asBoolInt(discoveryRule.automation_opportunity),
    calculate_cost_impact: asBoolInt(discoveryRule.calculate_cost_impact),
    filters: JSON.stringify(filters),
    issue_type: issueType,
    record_type: issueType,
    activity_type: apiProvider.getActivityType(),
    active: 'Y',
    save_variants: asBoolInt(discoveryRule.save_variants),
    save_activities: asBoolInt(discoveryRule.save_activities),
    save_steps: asBoolInt(discoveryRule.save_steps),
  };
};

const numberWithCommas = (x) => (x === -1 || x === undefined ? '' : Number(x).toLocaleString());
const formatPercentage = (x, decimals) => (x === -1 || x === undefined ? '' : (x * 100).toFixed(decimals) + '%');

const replaceQuotes = (obj) => {
  for (let key in obj) {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].replace(/"([^"]+)"/g, "'$1'");
    } else if (obj[key] && typeof obj[key] === 'object') {
      replaceQuotes(obj[key]);
    }
  }
  return obj;
};

const EXCLUDE_ATTRS = new Set(['Work Item #', 'Duration']);

export function computeKpis(sampleItems, issueType, affectedCount, affectedPct) {
  const durations = sampleItems
    .map((r) => Number(r.Duration))
    .filter((v) => Number.isFinite(v) && v >= 0)
    .sort((a, b) => a - b);

  const avg = durations.length === 0 ? undefined : Math.round(durations.reduce((s, v) => s + v, 0) / durations.length);

  const med =
    durations.length === 0
      ? undefined
      : durations.length % 2
      ? durations[(durations.length - 1) / 2]
      : Math.round((durations[durations.length / 2 - 1] + durations[durations.length / 2]) / 2);

  return {
    issue_type: issueType,
    affected_count: affectedCount,
    affected_pct: affectedPct,
    avg_duration_sec: avg,
    median_duration_sec: med,
  };
}

export function inferObservedPatterns(sampleItems, topN = 3) {
  if (!sampleItems?.length) return { attributes: [] };

  const keys = Object.keys(sampleItems[0]).filter((k) => !EXCLUDE_ATTRS.has(k));
  const attributes = keys.map((attr) => {
    const freq = new Map();
    for (const row of sampleItems) {
      const val = row[attr] ?? 'N/A';
      freq.set(val, (freq.get(val) || 0) + 1);
    }
    const total = sampleItems.length;
    const top = [...freq.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([val, cnt]) => `${val} (${((cnt / total) * 100).toFixed(1)}%)`)
      .join(', ');

    return {
      attribute: attr,
      observed_pattern: `Top ${Math.min(topN, freq.size)}: ${top}`,
      possible_interpretation: '',
    };
  });

  return { attributes };
}

export const DataGrid = ({ rows, type, AI }) => {
  try {
    if (typeof window !== 'undefined' && !window.__PA_SAVE_DISCOVERY_RULE_GRID_RENDERED__) {
      window.__PA_SAVE_DISCOVERY_RULE_GRID_RENDERED__ = true;
      traceSaveDiscoveryRule('grid-rendered', {
        appVersionProp: typeof AppVersion !== 'undefined' ? AppVersion : null,
        resolvedAppVersion: typeof getAppVersion === 'function' ? getAppVersion() : null,
        rows: Array.isArray(rows) ? rows.length : null,
        hasCustomerIdProp: !!(typeof customer_id !== 'undefined' && customer_id),
        hasRefreshProp: typeof refreshDiscoveryRules === 'function',
      });
    }
  } catch (_) {}

  const contentRef = useRef(null);
  const grid = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const [discoveryRuleText, setDiscoveryRuleText] = useState('');
  const [discoveryRule, setDiscoveryRule] = useState(null);
  const [insights, setInsights] = useState(null);

  const [requestId, setRequestId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const pollIntervalRef = useRef(null);
  const processingCtrRef = useRef(0);

  // Render the dashboard when insights are updated
  useEffect(() => {
    if (insights && contentRef.current) {
      try {
        // Expecting renderer signature: renderDiscoveryRuleDashboardFromJSON(data, container)
        renderDiscoveryRuleDashboardFromJSON(insights, contentRef.current);
      } catch (e) {
        console.error('Render error:', e);
        contentRef.current.innerHTML = `<pre style="color:#d1242f">Failed to render insights.</pre>`;
      }
    }
  }, [insights]);

  // Keep grid fresh on type change
  useEffect(() => {
    if (grid.current?.api) grid.current.api.refreshCells({ force: true });
  }, [type]);

  const onRowClickedFindingContext = useCallback((params) => {
    setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Evidence - Discovery Rules', 'Discovery Rule');
  }, []);

  // ESC to close modals
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        if (isExportOpen) setIsExportOpen(false);
        else if (isModalOpen) setIsModalOpen(false);
      }
    };
    if (isModalOpen || isExportOpen) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isModalOpen, isExportOpen]);

  // Poll status for Forge jobs
  useEffect(() => {
    if (requestId && (status?.status === 'pending' || status?.status === 'processing')) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const { invoke } = await loadForgeModule();
          const statusData = await invoke('checkStatus', { requestId });
          setStatus(statusData);
          updateProgressModal({ status: 'AI Insights - processing ' + processingCtrRef.current, ai: true  });
          processingCtrRef.current += 1;

          if (statusData.status === 'completed') {
            const json = JSON.parse(statusData.result);
            setInsights(json);
            setIsModalOpen(true);
            closeProgressModal();
            clearInterval(pollIntervalRef.current);
          } else if (statusData.status === 'failed') {
            setError(statusData.error || 'Request failed');
            closeProgressModal();
            clearInterval(pollIntervalRef.current);
          }
        } catch (err) {
          console.error('Error checking status:', err);
          setError('Failed to check status');
          closeProgressModal();
          clearInterval(pollIntervalRef.current);
        }
      }, 1000);

      return () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      };
    }
  }, [requestId, status?.status]);

  const headerName = React.useMemo(() => {
    if (type === 'performance') return 'Performance Rule';
    if (type === 'automation') return 'Automation Opportunities';
    if (type === 'compliance') return 'Compliance Rules';
    return 'Rule';
  }, [type]);

  // ---------- AI Insights ----------
  const AI_Insights = async (rowDiscoveryRule) => {
    const discoveryRuleSchema = {
      name: 'DiscoveryRuleAnalysis',
      schema: {
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        additionalProperties: false,
        properties: {
          summary: { type: 'string' },
          kpis: {
            type: 'object',
            additionalProperties: false,
            properties: {
              issue_type: { type: 'string' },
              affected_count: { type: 'integer' },
              affected_pct: { type: 'number' },
              avg_duration_sec: { type: 'number' },
              likely_primary_cause: { type: 'string' },
              key_signals: { type: 'array', items: { type: 'string' } },
            },
            required: [
              'issue_type',
              'affected_count',
              'affected_pct',
              'avg_duration_sec',
              'likely_primary_cause',
              'key_signals',
            ],
          },
          observed_patterns: {
            type: 'object',
            additionalProperties: false,
            properties: {
              attributes: {
                type: 'array',
                items: {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    attribute: { type: 'string' },
                    observed_pattern: { type: 'string' },
                    possible_interpretation: { type: 'string' },
                  },
                  required: ['attribute', 'observed_pattern', 'possible_interpretation'],
                },
              },
            },
            required: ['attributes'],
          },
          likely_primary_causes: { type: 'array', items: { type: 'string' } },
          root_cause_hypotheses: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: {
                hypothesis: { type: 'string' },
                supporting_evidence: { type: 'string' },
              },
              required: ['hypothesis', 'supporting_evidence'],
            },
          },
          optimization_plan: {
            type: 'object',
            additionalProperties: false,
            properties: {
              process_changes: { type: 'array', items: { type: 'string' } },
              jira_configuration: { type: 'array', items: { type: 'string' } },
              automation_rules: {
                type: 'array',
                items: {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    platform: { type: 'string' }, // Jira Automation | JMWE | ScriptRunner
                    rule: { type: 'string' },
                  },
                  required: ['platform', 'rule'],
                },
              },
              behavioural_nudges: { type: 'array', items: { type: 'string' } },
            },
            required: ['process_changes', 'jira_configuration', 'automation_rules', 'behavioural_nudges'],
          },
          risks_and_mitigations: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: { risk: { type: 'string' }, mitigation: { type: 'string' } },
              required: ['risk', 'mitigation'],
            },
          },
          key_signals: { type: 'array', items: { type: 'string' } },
        },
        required: [
          'summary',
          'kpis',
          'observed_patterns',
          'likely_primary_causes',
          'root_cause_hypotheses',
          'optimization_plan',
          'risks_and_mitigations',
          'key_signals',
        ],
      },
      strict: true,
    };

    setDiscoveryRuleText(rowDiscoveryRule.discovery_rule);
    setDiscoveryRule(rowDiscoveryRule);

    const version = getAppVersion();
    await openProgressModal({ status: version === 'FORGE' ? 'AI Insights - queueing request' : 'Generating AI Insights', ai: true  });
    await sleep(50);

    const issueTypes = getIssueTypes();
    const issueTypeStr = issueTypes.length === 1 ? `Issue Type = ${issueTypes[0]}` : `Issue Types = ${issueTypes.join(' or ')}`;
    const issueTypeForKpi = issueTypes.join(' | ');

    const discoveryRuleTypeMap = { Performance: 'performance discoveryRule', Compliance: 'compliance issue', Automation: 'automation opportunity' };
    const discoveryRuleTypeStr = discoveryRuleTypeMap[rowDiscoveryRule.category] || 'discovery_rule';

    const sampleItems = taskJSON(); // array of work items

    const discoveryRuleTextPrompt =
      `In a Jira process for ${issueTypeStr}, there was a ${discoveryRuleTypeStr} that ` +
      `${rowDiscoveryRule.tasks} ${apiProvider.getEntityUpper().toLowerCase()}s, representing ${(rowDiscoveryRule.percTasks * 100).toFixed(1)}% of all ${apiProvider.getEntityUpper().toLowerCase()}s, has ${rowDiscoveryRule.discovery_rule}. ` +
      `A sample of the ${apiProvider.getEntityUpper().toLowerCase()}s is in the following JSON. Duration is in seconds.`;

    const systemContent =
      'You are an expert in Jira Service Management workflow optimization. ' +
      'Produce a concise, executive-grade analysis that EXACTLY conforms to the DiscoveryRuleAnalysis schema. ' +
      'Use the provided KPIs and dynamic attribute summaries; refine interpretations but do not invent attributes. ' + JSON.stringify(discoveryRuleSchema);

    const kpisBase = computeKpis(sampleItems, issueTypeForKpi, rowDiscoveryRule.tasks, rowDiscoveryRule.percTasks * 100);
    const observed = inferObservedPatterns(sampleItems);
    console.log(observed)

    const userContent = [
      `DiscoveryRule: ${rowDiscoveryRule.discovery_rule}`,
      `Issue Types: ${issueTypes.join(', ')}`,
      `Affected: ${rowDiscoveryRule.tasks} items (${(rowDiscoveryRule.percTasks * 100).toFixed(1)}%)`,
      '',
      'Sample (JSON array):',
      JSON.stringify(sampleItems),
      '',
      'Precomputed KPIs:',
      JSON.stringify(kpisBase),
      '',
      'Observed attribute summaries (dynamic):',
      JSON.stringify(observed),
    ].join('\n');

    if (version === 'FORGE') {
      setError(null);
      setStatus(null);
      processingCtrRef.current = 1;
      try {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Discovery Rule AI',
          payload: { user: userContent, system: systemContent, aiProvider: getInvokeAIProvider(), model: getAIModelConfig().model, temperature: getAIModelConfig().temperature },
        });
        
        setRequestId(response.requestId);
        setStatus({ status: 'pending' });
      } catch (err) {
        console.error('Error submitting request:', err);
        setError('Failed to submit request');
        closeProgressModal();
      }
      return;
    }

    // Jira Cloud path
    if (false) {
      try {
        // NOTE: key preserved exactly as provided
        const API_KEY = undefined;
        if (!API_KEY) throw new Error('OpenAI API key not configured');

        const resp = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_KEY}` },
          body: JSON.stringify({
            model: 'gpt-4o-2024-08-06',
            temperature: 0.2,
            messages: [
              { role: 'system', content: 'You must output valid JSON only. Do not include backticks. Strings must use double quotes; if inner quotes exist, convert them to single quotes.' },
              { role: 'system', content: systemContent },
              { role: 'user', content: discoveryRuleTextPrompt + '\n\n' + userContent },
            ],
          }),
        });

        const data = await resp.json();
        const text = data?.choices?.[0]?.message?.content ?? '{}';
        const parsed = JSON.parse(text);
        replaceQuotes(parsed);
        setInsights(parsed);
        setIsModalOpen(true);
      } catch (err) {
        console.error('Error generating insights:', err);
        setError('Failed to generate insights. Please try again.');
      } finally {
        closeProgressModal();
      }
    }
  };

  const saveDiscoveryRuleType = async (discoveryRule, type) => {
    traceSaveDiscoveryRule('action-start', {
      type,
      rowKeys: Object.keys(discoveryRule || {}),
      rowPreview: safeDebugPreview(discoveryRule),
    });

    await openProgressModal({ status: 'Saving Discovery Rule' });
    await sleep(50);

    let payload = null;

    try {
      const appVersion = typeof getAppVersion === 'function' ? getAppVersion() : 'FORGE';
      payload = cloneDiscoveryRuleForGlobalSave(discoveryRule || {}, type);

      traceSaveDiscoveryRule('payload-built', {
        appVersion,
        type,
        payloadPreview: safeDebugPreview(payload),
        discoveryRule: payload?.discovery_rule,
        issueType: payload?.issue_type,
        category: payload?.category,
        ruleType: payload?.discovery_rule_type,
        filtersType: typeof payload?.filters,
        filtersLength: String(payload?.filters || '').length,
      });

      if (!payload?.discovery_rule) {
        throw new Error('Discovery Rule text is empty or missing on the selected row.');
      }

      if (appVersion === 'FORGE') {
        traceSaveDiscoveryRule('forge-invoke-start', { functionName: 'createDiscoveryRule' });
        const { invoke } = await loadForgeModule();
        const result = await invoke('createDiscoveryRule', payload);
        traceSaveDiscoveryRule('forge-invoke-success', { resultPreview: safeDebugPreview(result) });
      } else if (appVersion === 'SNOW') {
        traceSaveDiscoveryRule('snow-api-resolve-start');
        const snApi = await getSNOWApi();
        traceSaveDiscoveryRule('snow-api-resolved', {
          hasSnApi: !!snApi,
          availableMethods: snApi ? Object.keys(snApi).filter(k => String(k).toLowerCase().includes('discovery')).sort() : [],
          createDiscoveryRuleType: typeof snApi?.createDiscoveryRule,
        });

        if (!snApi || typeof snApi.createDiscoveryRule !== 'function') {
          throw new Error('ServiceNow API is not available for saving global Discovery Rules.');
        }

        traceSaveDiscoveryRule('snow-create-start', { payloadPreview: safeDebugPreview(payload) });
        const result = await snApi.createDiscoveryRule(payload);
        traceSaveDiscoveryRule('snow-create-success', { resultPreview: safeDebugPreview(result) });
      } else {
        throw new Error(`Unsupported app version: ${appVersion || 'unknown'}`);
      }

      if (typeof apiProvider.setDiscoveryRulesEdited === 'function') {
        apiProvider.setDiscoveryRulesEdited({ edited: true });
        traceSaveDiscoveryRule('set-edited-called');
      } else {
        traceSaveDiscoveryRule('set-edited-missing');
      }

      if (typeof apiProvider.updateDiscoveryRules === 'function') {
        apiProvider.updateDiscoveryRules({});
        traceSaveDiscoveryRule('update-discovery-rules-called');
      } else {
        traceSaveDiscoveryRule('update-discovery-rules-missing');
      }

      if (typeof apiProvider.alertMessage === 'function') {
        apiProvider.alertMessage({
          message: 'Discovery Rule saved',
          severity: 'success',
          showAlert: true,
          timeOutActive: true,
          component: 'pathConstraint',
        });
      }

      traceSaveDiscoveryRule('action-success');
    } catch (err) {
      traceSaveDiscoveryRule('action-error', {
        message: err?.message || String(err),
        stack: err?.stack || '',
        payloadPreview: safeDebugPreview(payload),
      });
      console.error('[PA Save Discovery Rule] failed', err);

      if (typeof apiProvider.alertMessage === 'function') {
        apiProvider.alertMessage({
          message: `Save Discovery Rule failed: ${err?.message || err}`,
          severity: 'error',
          showAlert: true,
          timeOutActive: true,
          component: 'pathConstraint',
        });
      }
    } finally {
      traceSaveDiscoveryRule('action-finish');
      closeProgressModal();
    }
  };

  // ---------- Grid helpers ----------
  const menuLabel = (symbol, label) => label;
  const menuIcon = (symbol) => `<span class="pa-ag-menu-icon">${symbol}</span>`;

  const getContextMenuItems = useCallback((params) => {
    traceSaveDiscoveryRule('context-menu-built', {
      rowKeys: Object.keys(params?.node?.data || {}),
      rowPreview: safeDebugPreview(params?.node?.data || {}),
    });

    const saveDiscoveryRule = [
      {
        name: 'Save Discovery Rule',
        icon: `<img src="${SaveIcon}" />`,
        subMenu: [
          {
            name: 'Record Type',
            action: () => {
              traceSaveDiscoveryRule('menu-click-record-type');
              return saveDiscoveryRuleType(params?.node?.data || {}, 'issueType');
            },
            icon: `<img src="${SaveIcon}" />`,
          },
          {
            name: 'Generic',
            action: () => {
              traceSaveDiscoveryRule('menu-click-generic');
              return saveDiscoveryRuleType(params?.node?.data || {}, 'generic');
            },
            icon: `<img src="${SaveIcon}" />`,
          },
        ],
      },
    ];

    const process = [
      {
        name: 'Process',
        action: async () => {
          await openProgressModal({ status: 'Process DiscoveryRule' });
          await sleep(50);
          apiProvider.setAction_Visualizer({});
          apiProvider.processDiscoveryRuleDataGrid({ discovery_rule: params.node.data });
          closeProgressModal();
        },
        icon: `<img src="${RefreshIcon}" />`,
      },
    ];

    const insightsMenu = [
      {
        name: menuLabel('◆', 'Insights'), icon: menuIcon('◆'),
        icon: `<img src="${AnalyticsIcon}" />`,
        subMenu: [
          {
            name: menuLabel('●', 'Clusters'), icon: menuIcon('●'),
            action: async () => {
              await openProgressModal({ status: 'Processing Clusters' });
              await sleep(50);
              await apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              await apiProvider.setDiagramCluster({ source: 'discovery_rule', discovery_rule_id: params.node.data.discovery_rule_id });
              closeProgressModal();
            },
            icon: `<img src="${ClusterIcon}" />`,
          },
          {
            name: menuLabel('↗', 'Influencers'), icon: menuIcon('↗'),
            action: async () => {
              await openProgressModal({ status: 'Processing Influencer Insights' });
              await sleep(50);
              await apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              await apiProvider.setDiagramInfluenceAnalysis({ source: 'discovery_rule', discovery_rule_id: params.node.data.discovery_rule_id });
              closeProgressModal();
            },
            icon: `<img src="${InfluencerIcon}" />`,
          },
          {
            name: menuLabel('▦', 'Decompose'), icon: menuIcon('▦'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setDiagramDecompositionTree({ source: 'discovery_rule' });
            },
            icon: `<img src="${DecomposeIcon}" />`,
          },
          {
            name: menuLabel('◷', 'Activity Analysis'), icon: menuIcon('◷'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setTimeInActivitySource({ source: 'discovery_rule' });
              apiProvider.setAction_Time({});
            },
            icon: `<img src="${TaskLifeCycleIcon}" />`,
          },
          {
            name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
            action: () => {
              apiProvider.generateHistogramArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.histogram({});
            },
            icon: `<img src="${HistogramIcon}" />`,
          },
        ],
      },
      {
        name: `${apiProvider.getEntityUpper()}s`,
        icon: `<img src="${TaskIcon}" />`,
        subMenu: [
          {
            name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
            action: () => {
              const discovery_rule_id = params.node.data.discovery_rule_id;
              apiProvider.setDiscoveryRuleDetailProps({ discoveryRule: discovery_rule_id });
              apiProvider.taskDetail({});
            },
            icon: `<img src="${DetailIcon}" />`,
          },
          {
            name: menuLabel('↻', 'History'), icon: menuIcon('↻'),
            action: () => {
              const discovery_rule_id = `f-${params.node.data.discovery_rule_id}`;
              apiProvider.processAnalytics({
                discovery_rule_id: discovery_rule_id,
                discovery_rule_type: params.node.data.category,
                object_type: 'DiscoveryRule',
                object_name: params.node.data.step,
                series_name: `${apiProvider.getEntityUpper()}s`,
              });
            },
            icon: `<img src="${HistogramIcon}" />`,
          },
          {
            name: menuLabel('⟳', 'LifeCycle'), icon: menuIcon('⟳'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setTasksVisualizationSource({ source: 'discovery_rule' });
              apiProvider.tasksVisualization();
              apiProvider.taskLifecycle({});
            },
            icon: `<img src="${TaskLifeCycleIcon}" />`,
          },
        ],
      },
      'separator',
      {
        name: menuLabel('⇩', 'Export'), icon: menuIcon('⇩'),
        icon: `<img src="${ExportIcon}" />`,
        subMenu: [
          { name: menuLabel('CSV', 'CSV Export (all)'), icon: menuIcon('CSV'), action: () => params.api.exportDataAsCsv({ onlySelected: false }), icon: `<img src="${CSVIcon}" />` },
          { name: menuLabel('CSV', 'CSV Export (selected)'), icon: menuIcon('CSV'), action: () => params.api.exportDataAsCsv({ onlySelected: true }), icon: `<img src="${CSVIcon}" />` },
          { name: menuLabel('XLS', 'XLS Export (all)'), icon: menuIcon('XLS'), action: () => params.api.exportDataAsExcel({ onlySelected: false }), icon: `<img src="${ExcelIcon}" />` },
          { name: menuLabel('XLS', 'XLS Export (selected)'), icon: menuIcon('XLS'), action: () => params.api.exportDataAsExcel({ onlySelected: true }), icon: `<img src="${ExcelIcon}" />` },
        ],
      },
      'copy',
    ];

    return [...saveDiscoveryRule, 'separator', ...process, ...insightsMenu];
  }, []);

  const getCellStyle = (value) => {
    const base = { fontSize: '14px', fontWeight: '600', textAlign: 'right' };
    if (value > 0.5) return { ...base, color: '#DE350B', backgroundColor: '#FFEBE6' };
    if (value > 0.2) return { ...base, color: '#FF8B00', backgroundColor: '#FFF4E6' };
    if (value > 0) return { ...base, color: '#00875A', backgroundColor: '#E3FCEF' };
    return { ...base, color: '#6B778C' };
  };

const columnDefs = React.useMemo(
  () => [
    ...(AI ? [{
      field: 'aiInsights',
      headerName: 'AI Insights',
      width: 130,
      suppressMenu: true,
      cellRenderer: (params) => {
        const click = (e) => {
          e.stopPropagation();
          AI_Insights(params.data);
        };
        return (
          <div style={{ display: 'flex', height: '100%', cursor: 'pointer', paddingTop: 5, paddingLeft: 10 }} onClick={click} title="AI Insights">
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="buttonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
              <rect width="50" height="32" rx="16" fill="url(#buttonGradient)" />
              <g transform="translate(13, 4) scale(1)">
                <path d="M12 2.5C8.4 2.5 5.5 5.4 5.5 9c0 2.4 1.3 4.5 3.2 5.6v1.9c0 .8.7 1.5 1.5 1.5h3.6c.8 0 1.5-.7 1.5-1.5v-1.9c1.9-1.1 3.2-3.2 3.2-5.6 0-3.6-2.9-6.5-6.5-6.5z" fill="#FFD54F" />
                <rect x="10.3" y="18" width="3.4" height="1.5" fill="#E65100" rx="0.7" />
                <circle cx="12" cy="8" r="2" fill="#FFFFFF" opacity="0.4" />
              </g>
            </svg>
          </div>
        );
      },
      sortable: false,
      filter: false,
      resizable: false,
    }] : []),
    { field: 'discovery_rule_id', headerName: 'DiscoveryRule ID', hide: true },
    {
      field: 'discovery_rule',
      headerName,
      resizable: true,
      wrapText: true,
      autoHeight: true,
      flex: 1,
      minWidth: 250,
      cellRenderer: 'agGroupCellRenderer',
      pinnedRowCellRenderer: (p) => `<strong>${p.data.discovery_rule}</strong>`,
    },
    { field: 'tasks', headerName: apiProvider.getEntityUpper() + 's', filter: 'agNumberColumnFilter', width: 140, valueFormatter: ({ value }) => numberWithCommas(value) },
    { field: 'percTasks', headerName: '% ' + apiProvider.getEntityUpper() + 's', filter: 'agNumberColumnFilter', width: 160, valueFormatter: ({ value }) => formatPercentage(value, 1), cellStyle: (p) => getCellStyle(p.value) },
    { field: 'events', headerName: 'Events', filter: 'agNumberColumnFilter', width: 100, type: 'rightAligned', valueFormatter: ({ value }) => numberWithCommas(value) },
    { field: 'percEvents', headerName: '% Events', filter: 'agNumberColumnFilter', width: 115, type: 'rightAligned', valueFormatter: ({ value }) => formatPercentage(value, 1), cellStyle: (p) => getCellStyle(p.value) },
    { field: 'avgImpactDurationTasks', headerName: 'Avg Impact Duration', filter: 'agNumberColumnFilter', width: 175, type: 'rightAligned', valueFormatter: ({ value }) => apiProvider.formatDuration({ seconds: value }) },
  ],
  [headerName, AI]
);

  const download = (filename, mime, content) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const exportToHTML = () => {
    if (!insights || !contentRef.current) return;
    
    // Get the exact rendered HTML from the content div
    const renderedHTML = contentRef.current.innerHTML;
    
    // Get computed styles from the content element to preserve styling
    const computedStyles = window.getComputedStyle(contentRef.current);
    const contentStyles = `
      font-family: ${computedStyles.fontFamily};
      color: ${computedStyles.color};
      background: ${computedStyles.background};
      padding: ${computedStyles.padding};
    `;
    
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(discoveryRuleText || 'Analysis Report')}</title>
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      margin: 0;
      padding: 24px;
      color: #172b4d;
    }
    h1 {
      font-size: 20px;
      margin: 0 0 20px;
      font-weight: 600;
    }
    .content {
      ${contentStyles}
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(discoveryRuleText || 'Analysis Report')}</h1>
  <div class="content">
    ${renderedHTML}
  </div>
</body>
</html>`;
    
    download(`${(discoveryRuleText || 'analysis').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`, 'text/html', html);
    setIsExportOpen(false);
  };

  const exportToJSON = () => {
    if (!insights) return;
    download(`${(discoveryRuleText || 'analysis').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`, 'application/json', JSON.stringify(insights, null, 2));
    setIsExportOpen(false);
  };

  return (
    <Wrapper>
      <GridWrapper>
        <AgGridReact
          onRowClicked={onRowClickedFindingContext}
          theme={themeAlpine}
          rowSelection="single"
          columnDefs={columnDefs}
          allowContextMenuWithControlKey
          getContextMenuItems={getContextMenuItems}
          headerHeight={40}
          ref={grid}
          defaultColDef={{ sortable: true, filter: true, resizable: true, wrapText: true, autoHeight: true }}
          animateRows
          rowData={rows}
        />
      </GridWrapper>

      {isModalOpen && (
        <Overlay>
          <Modal>
            <Header>
              <ModalTitle>DiscoveryRule: {discoveryRuleText}</ModalTitle>
              <HeaderButtons>
                <HeaderButton onClick={() => AI_Insights(discoveryRule)} disabled={!discoveryRule}>Refresh</HeaderButton>
                <HeaderButton onClick={() => setIsExportOpen(true)} disabled={!insights}>Export…</HeaderButton>
                <CloseButton onClick={() => setIsModalOpen(false)} aria-label="Close dialog">
                  <X size={18} color="#6b7280" />
                </CloseButton>
              </HeaderButtons>
            </Header>
            <Content ref={contentRef} />
          </Modal>
        </Overlay>
      )}

      {isExportOpen && (
        <Overlay onClick={() => setIsExportOpen(false)}>
          <ExportSheet onClick={(e) => e.stopPropagation()}>
            <ExportHeader>
              <ExportTitle>Export Insights</ExportTitle>
              <CloseButton onClick={() => setIsExportOpen(false)}>
                <X size={18} color="#6b7280" />
              </CloseButton>
            </ExportHeader>
            <ExportBody>
              <ExportLabel>Choose your preferred export format:</ExportLabel>

              <ExportOption onClick={exportToHTML}>
                <ExportIconSvg viewBox="0 0 24 24" fill="#0052cc">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                </ExportIconSvg>
                <ExportText>
                  <ExportName>Export as HTML</ExportName>
                  <ExportDesc>Generate a printable HTML document</ExportDesc>
                </ExportText>
              </ExportOption>

              <ExportOption onClick={exportToJSON}>
                <ExportIconSvg viewBox="0 0 24 24" fill="#ff991f">
                  <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
                </ExportIconSvg>
                <ExportText>
                  <ExportName>Export as JSON</ExportName>
                  <ExportDesc>Download raw data for further analysis</ExportDesc>
                </ExportText>
              </ExportOption>
            </ExportBody>
            <ExportFooter>
              <CancelButton onClick={() => setIsExportOpen(false)}>Cancel</CancelButton>
            </ExportFooter>
          </ExportSheet>
        </Overlay>
      )}
    </Wrapper>
  );
};